/**
 * Enhanced Comments Panel
 * 
 * Extends comment functionality with threading, types, priorities,
 * and filtering capabilities.
 */

import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card } from '../UI/Card';
import { Button } from '../UI/Button';
import { Badge } from '../UI/Badge';
import { LoadingSpinner } from '../UI/LoadingSpinner';
import { Input } from '../UI/Input';
import { Select } from '../UI/Select';
import { 
  MessageSquare, 
  Send, 
  Reply, 
  CheckCircle,
  Filter,
  X
} from 'lucide-react';
import { 
  getReportComments,
  addReportComment,
  resolveReportComment,
  getCommentsByType,
  getCommentsByPriority,
  getReportSections,
  type ReportComment,
  type CommentType,
  type CommentPriority
} from '../../api/reports';
import { useWorkspace } from '../../hooks/useWorkspace';
import { getPolicyPack } from '../../api/policy';
import { getProject } from '../../api/projects';

export interface EnhancedCommentsPanelProps {
  reportId: string;
  onCommentChange?: () => void;
}

export function EnhancedCommentsPanel({ 
  reportId, 
  onCommentChange 
}: EnhancedCommentsPanelProps) {
  const { projectId } = useWorkspace();
  const queryClient = useQueryClient();
  const [newComment, setNewComment] = useState('');
  const [selectedSection, setSelectedSection] = useState('');
  const [parentCommentId, setParentCommentId] = useState<string | undefined>();
  const [commentType, setCommentType] = useState<CommentType>('general');
  const [priority, setPriority] = useState<CommentPriority | ''>('');
  // Always use thread view (removed toggle buttons - comments always display in threaded/nested format)
  const [filterType, setFilterType] = useState<string>('all');
  const [filterPriority, setFilterPriority] = useState<string>('all');
  const [filterSection, setFilterSection] = useState<string>('all');
  const [commentTypes, setCommentTypes] = useState<Record<string, any>>({
    // Default comment types (fallback if policy pack doesn't define them)
    general: { name: 'General' },
    suggestion: { name: 'Suggestion' },
    correction: { name: 'Correction' },
    clarification: { name: 'Clarification' },
    compliance_issue: { name: 'Compliance Issue' },
    quality_concern: { name: 'Quality Concern' },
  });

  // Load policy pack for comment types
  useEffect(() => {
    if (!projectId) return;
    
    // First fetch project to get policy_pack_id
    getProject(projectId).then((project) => {
      if (project.policy_pack_id) {
        return getPolicyPack(project.policy_pack_id);
      }
      return null;
    }).then((pack) => {
      if (pack) {
        const reviewWorkflow = pack?.review_workflow;
        if (reviewWorkflow?.comment_types) {
          setCommentTypes(reviewWorkflow.comment_types);
        }
      }
    }).catch((err) => {
      console.warn('[Enhanced Comments] Failed to load policy pack:', err);
    });
  }, [projectId]);

  // Fetch report sections for dropdown
  const { data: sectionsData } = useQuery({
    queryKey: ['report-sections', reportId],
    queryFn: () => getReportSections(reportId, { includeCustom: true, orderBy: 'display_order' }),
    enabled: !!reportId,
  });

  const reportSections = sectionsData?.sections || [];

  // Get comments
  const { data: comments, isLoading: commentsLoading } = useQuery({
    queryKey: ['report-comments', reportId, filterType, filterPriority, filterSection],
    queryFn: () => {
      if (filterType !== 'all' && filterType !== '') {
        return getCommentsByType(reportId, filterType as CommentType);
      }
      if (filterPriority !== 'all' && filterPriority !== '') {
        return getCommentsByPriority(reportId, filterPriority as CommentPriority);
      }
      return getReportComments(reportId, filterType !== 'all' ? filterType as CommentType : undefined, filterPriority !== 'all' ? filterPriority as CommentPriority : undefined, filterSection !== 'all' ? filterSection : undefined);
    },
    enabled: !!reportId,
  });

  // Add comment mutation
  const addCommentMutation = useMutation({
    mutationFn: () => addReportComment(
      reportId,
      newComment,
      selectedSection || undefined,
      parentCommentId,
      commentType,
      priority !== '' ? priority as CommentPriority : undefined
    ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['report-comments', reportId] });
      setNewComment('');
      setSelectedSection('');
      setParentCommentId(undefined);
      setCommentType('general');
      setPriority('');
      if (onCommentChange) {
        onCommentChange();
      }
    },
    onError: (error: any) => {
      alert(`Failed to add comment: ${error.message || 'Unknown error'}`);
    },
  });

  // Resolve comment mutation
  const resolveMutation = useMutation({
    mutationFn: (commentId: string) => resolveReportComment(reportId, commentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['report-comments', reportId] });
      if (onCommentChange) {
        onCommentChange();
      }
    },
  });

  const handleSubmitComment = () => {
    if (!newComment.trim()) {
      alert('Please enter a comment');
      return;
    }
    addCommentMutation.mutate();
  };

  const handleReply = (parentId: string) => {
    setParentCommentId(parentId);
    // Scroll to comment form
    document.getElementById('comment-form')?.scrollIntoView({ behavior: 'smooth' });
  };

  // Build comment tree for thread view
  const buildCommentTree = (comments: ReportComment[]): Array<ReportComment & { replies?: ReportComment[] }> => {
    const commentMap = new Map<string, ReportComment & { replies?: ReportComment[] }>();
    const rootComments: Array<ReportComment & { replies?: ReportComment[] }> = [];

    // First pass: create map
    comments.forEach(comment => {
      commentMap.set(comment.id, { ...comment, replies: [] });
    });

    // Second pass: build tree
    comments.forEach(comment => {
      const commentWithReplies = commentMap.get(comment.id)!;
      if (comment.parent_comment_id) {
        const parent = commentMap.get(comment.parent_comment_id);
        if (parent) {
          if (!parent.replies) parent.replies = [];
          parent.replies.push(commentWithReplies);
        }
      } else {
        rootComments.push(commentWithReplies);
      }
    });

    return rootComments;
  };

  // Get unique sections for filter
  const sections = Array.from(new Set(comments?.map(c => c.section).filter(Boolean) || []));

  if (commentsLoading) {
    return (
      <Card>
        <div className="flex items-center justify-center py-12">
          <LoadingSpinner />
          <span className="ml-3 text-gray-600 dark:text-gray-400">Loading comments...</span>
        </div>
      </Card>
    );
  }

  const typeColors: Record<string, string> = {
    suggestion: 'info',
    correction: 'warning',
    clarification: 'default',
    compliance_issue: 'error',
    quality_concern: 'warning',
    general: 'default',
  };

  const priorityColors: Record<string, string> = {
    low: 'default',
    medium: 'warning',
    high: 'error',
  };

  // Always use thread view (nested comments)
  const displayComments = comments 
    ? buildCommentTree(comments)
    : [];

  const renderComment = (comment: ReportComment, depth: number = 0) => (
    <div
      key={comment.id}
      className={`p-4 border border-theme-border rounded-lg bg-theme-surface ${depth > 0 ? 'ml-8 mt-2' : 'mb-3'}`}
    >
      <div className="flex items-start justify-between mb-2">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-medium">{comment.user_name}</span>
          {comment.comment_type && (
            <Badge variant={typeColors[comment.comment_type] || 'default'} size="sm">
              {comment.comment_type}
            </Badge>
          )}
          {comment.priority && (
            <Badge variant={priorityColors[comment.priority] || 'default'} size="sm">
              {comment.priority}
            </Badge>
          )}
          {comment.section && (
            <Badge variant="info" size="sm">
              {comment.section}
            </Badge>
          )}
          {comment.resolved && (
            <Badge variant="success" size="sm">
              Resolved
            </Badge>
          )}
        </div>
        <div className="flex gap-2">
          {!comment.resolved && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => handleReply(comment.id)}
            >
              <Reply className="h-3 w-3" />
            </Button>
          )}
          {!comment.resolved && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => resolveMutation.mutate(comment.id)}
            >
              <CheckCircle className="h-3 w-3" />
            </Button>
          )}
        </div>
      </div>
      <p className="text-sm text-gray-700 dark:text-gray-300 mb-2">{comment.content}</p>
      <div className="text-xs text-gray-500 dark:text-gray-400">
        {new Date(comment.created_at).toLocaleString()}
      </div>
      {(comment as any).replies && (comment as any).replies.length > 0 && (
        <div className="mt-3">
          {(comment as any).replies.map((reply: ReportComment) => renderComment(reply, depth + 1))}
        </div>
      )}
    </div>
  );

  return (
    <Card>
      <div className="mb-6">
        <h3 className="text-lg font-semibold">Comments</h3>
      </div>

      {/* Filters */}
      <div className="mb-4 flex gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Filter className="h-4 w-4 text-gray-500" />
          <Select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            options={[
              { value: 'all', label: 'All Types' },
              ...Object.entries(commentTypes).map(([key, value]: [string, any]) => ({
                value: key,
                label: value.name || key,
              })),
            ]}
          />
        </div>
        <Select
          value={filterPriority}
          onChange={(e) => setFilterPriority(e.target.value)}
          options={[
            { value: 'all', label: 'All Priorities' },
            { value: 'low', label: 'Low' },
            { value: 'medium', label: 'Medium' },
            { value: 'high', label: 'High' },
          ]}
        />
        {sections.length > 0 && (
          <Select
            value={filterSection}
            onChange={(e) => setFilterSection(e.target.value)}
            options={[
              { value: 'all', label: 'All Sections' },
              ...sections.map((section) => ({ value: section, label: section })),
            ]}
          />
        )}
      </div>

      {/* Comments List */}
      <div className="mb-6 space-y-3 max-h-96 overflow-y-auto">
        {displayComments.length > 0 ? (
          (displayComments as any[]).map((comment) => renderComment(comment))
        ) : (
          <div className="text-center py-8 text-gray-600 dark:text-gray-400">
            <MessageSquare className="h-12 w-12 mx-auto mb-3 opacity-50" />
            <p>No comments yet</p>
          </div>
        )}
      </div>

      {/* Add Comment Form */}
      <div id="comment-form" className="pt-6 border-t border-theme-border space-y-4">
        {parentCommentId && (
          <div className="p-2 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded flex items-center justify-between">
            <span className="text-sm text-blue-900 dark:text-blue-100">
              Replying to comment
            </span>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setParentCommentId(undefined)}
            >
              <X className="h-3 w-3" />
            </Button>
          </div>
        )}
        
        <div>
          <label className="block text-sm font-medium mb-2">Section (Optional)</label>
          <Select
            value={selectedSection}
            onChange={(e) => setSelectedSection(e.target.value)}
            options={[
              { value: '', label: 'General Comment (No Section)' },
              ...reportSections.map((section) => ({
                value: section.section_name,
                label: section.section_name,
              })),
            ]}
          />
        </div>

        <div>
          <label className="block text-sm font-medium mb-2">Comment *</label>
          <textarea
            className="w-full px-3 py-2 border border-theme-border rounded-lg bg-white dark:bg-theme-paper text-gray-900 dark:text-theme-text focus:outline-none focus:ring-2 focus:ring-primary-500"
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            placeholder="Add a comment..."
            rows={3}
            required
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-2">Type</label>
            <Select
              value={commentType}
              onChange={(e) => setCommentType(e.target.value as CommentType)}
              options={Object.entries(commentTypes).map(([key, value]: [string, any]) => ({
                value: key,
                label: value.name || key,
              }))}
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Priority (Optional)</label>
            <Select
              value={priority}
              onChange={(e) => setPriority(e.target.value as CommentPriority | '')}
              options={[
                { value: '', label: 'None' },
                { value: 'low', label: 'Low' },
                { value: 'medium', label: 'Medium' },
                { value: 'high', label: 'High' },
              ]}
            />
          </div>
        </div>

        <Button
          variant="primary"
          onClick={handleSubmitComment}
          disabled={addCommentMutation.isPending || !newComment.trim()}
        >
          <Send className="h-4 w-4 mr-2" />
          {addCommentMutation.isPending ? 'Adding...' : parentCommentId ? 'Reply' : 'Add Comment'}
        </Button>
      </div>
    </Card>
  );
}
